import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  setRouter,
  useScreenDetail,
  permissions,
  remCss,
  lang,
  scssVariables as variables,
  upperFirstCharAll,
  userFullName
} from 'src/helpers';
import model from './model';
import { Logo, MCButton, Spinner, View } from 'src/components/basic';
import Doctor from './Doctor';
import Clinic from './Clinic';
import LogoAvatar from 'src/assets/images/logo192.png';
import LANG_CONST from 'src/helpers/lang/const';
import errorDescriptions from 'src/constants/errors';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import AlertManager from 'src/components/alert/AlertManager';
import constants from 'src/constants';

const NotFound = ({ screen }) => (
  <div className={'mc-container-main mc-not-found'}>
    <div className={'header'}>
      <Link to={'/'} className={'mc-flex-15'}>
        <Logo screenDetail={screen} />
      </Link>
    </div>
    <div className={'mc-main'}>
      <div className='content-wrapper'>
        <div className='text-content'>
          <div className='error-badge'>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <circle cx='12' cy='12' r='10'></circle>
              <line x1='12' y1='8' x2='12' y2='12'></line>
              <line x1='12' y1='16' x2='12.01' y2='16'></line>
            </svg>
            {lang.t(LANG_CONST.not_found_obj.error)}
          </div>

          <h1 className='main-title'>
            {lang.t(LANG_CONST.not_found_obj.title_before)}{' '}
            <span className='gradient-text'>
              {lang.t(LANG_CONST.not_found_obj.title_highlight)}
            </span>{' '}
            {lang.t(LANG_CONST.not_found_obj.title_after)}
          </h1>

          <p className='subtitle'>{lang.t(LANG_CONST.not_found_obj.description)}</p>

          <div className='cta-section'>
            <div className='button-row'>
              <MCButton
                primary
                dark
                radius
                label={LANG_CONST.buttons.home}
                to={'/'}
                type={'link'}
                large
                style={{
                  borderRadius: remCss(16),
                  paddingHorizontal: remCss(43),
                  paddingVertical: remCss(17)
                }}
              />
              <MCButton
                primary
                dark
                radius
                type={'link'}
                label={LANG_CONST.buttons.find_doctor}
                to={'/discovery'}
                large
                style={{
                  borderRadius: remCss(16),
                  paddingHorizontal: remCss(43),
                  paddingVertical: remCss(17)
                }}
              />
            </div>

            <div className='help-text'>
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
              >
                <circle cx='12' cy='12' r='10'></circle>
                <path d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3'></path>
                <line x1='12' y1='17' x2='12.01' y2='17'></line>
              </svg>
              {lang.t(LANG_CONST.need_help)}{' '}
              <MCButton
                onClick={() => {
                  AlertManager.popup(AlertManager.TYPES.INFO, {
                    title: LANG_CONST.contact_us_obj.title,
                    icon: false,
                    width: remCss(450),
                    titleEx: (
                      <div className={'mc-flex-15-col'}>
                        {[
                          LANG_CONST.contact_us_obj.via_email,
                          LANG_CONST.contact_us_obj.via_phone,
                          LANG_CONST.contact_us_obj.via_link
                        ].map((txt, i) => (
                          <div className={'mc-flex-15'} key={i}>
                            <View className={''}>
                              <SVGIcon
                                circle
                                outline
                                light
                                padding={5}
                                iconname={BASE_ICONS.CHECK}
                                width={24}
                                fill={variables['colors-mc-primary']}
                              />
                            </View>
                            <div className={'mc-label-420'}>{lang.t(txt)}</div>
                          </div>
                        ))}
                      </div>
                    ),
                    cancelButton: {
                      label: LANG_CONST.buttons.close,
                      action: (actions) => {
                        actions.hide();
                      }
                    }
                  });
                }}
                label={LANG_CONST.buttons.contact_support}
                primary
                small
                style={{ padding: 0 }}
                transparent
              />
            </div>
          </div>
        </div>

        <div className='illustration-side'>
          <div className='floating-element element-1'>
            <div className='pill-capsule'></div>
          </div>
          <div className='floating-element element-2'>
            <div className='pill-capsule' style={{ width: 50, height: 25 }}></div>
          </div>

          <svg
            className='main-illustration'
            viewBox='0 0 600 600'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g opacity='0'>
              <animate
                attributeName='opacity'
                values='0;1'
                dur='0.8s'
                begin='0.3s'
                fill='freeze'
              ></animate>
              <circle cx='300' cy='300' r='120' fill='url(#glow)' opacity='0.3'></circle>
              <defs>
                <radialGradient id='glow' cx='50%' cy='50%'>
                  <stop offset='0%' stopColor='#00d4c8' stopOpacity='0.8'></stop>
                  <stop offset='100%' stopColor='#00d4c8' stopOpacity='0'></stop>
                </radialGradient>
              </defs>

              <circle
                cx='300'
                cy='300'
                r='140'
                stroke='#00d4c8'
                strokeWidth='3'
                fill='none'
                opacity='0.3'
                strokeDasharray='10 5'
              >
                <animateTransform
                  attributeName='transform'
                  type='rotate'
                  from='0 300 300'
                  to='360 300 300'
                  dur='20s'
                  repeatCount='indefinite'
                ></animateTransform>
              </circle>

              <rect
                x='260'
                y='220'
                width='80'
                height='160'
                rx='12'
                fill='url(#crossGradient)'
              ></rect>
              <rect
                x='220'
                y='260'
                width='160'
                height='80'
                rx='12'
                fill='url(#crossGradient)'
              ></rect>
              <defs>
                <linearGradient id='crossGradient' x1='0%' y1='0%' x2='100%' y2='100%'>
                  <stop offset='0%' stopColor='#00d4c8'></stop>
                  <stop offset='100%' stopColor='#33e0d5'></stop>
                </linearGradient>
              </defs>

              <g transform='translate(300, 300)'>
                <path
                  d='M-40 0 L-30 0 L-25 -15 L-20 15 L-15 0 L40 0'
                  stroke='white'
                  strokeWidth='4'
                  fill='none'
                  strokeLinecap='round'
                >
                  <animate
                    attributeName='stroke-dasharray'
                    values='0,200;200,0'
                    dur='2s'
                    repeatCount='indefinite'
                  ></animate>
                </path>
              </g>
            </g>
          </svg>
        </div>
      </div>
    </div>

    <div className='gradient-bg'>
      <div className='gradient-blob blob-1'></div>
      <div className='gradient-blob blob-2'></div>
      <div className='gradient-blob blob-3'></div>
    </div>
  </div>
);

const Profile = () => {
  const [error, setError] = useState(null);
  const [errorUsername, setErrorUsername] = useState(null);
  const [detail, setDetail] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const screen = useScreenDetail();
  const invalidUsername = params.username?.toString().length < 3;
  const isAllProfile = params.username === 'all';
  const allProfileDetail = isAllProfile
    ? {
        username: params.username,
        firstName: 'LifeApp',
        description: LANG_CONST.text.lifeapp_des,
        avatar: LogoAvatar,
        doctorsSpecialist: Object.keys(LANG_CONST.constants.specialist)
      }
    : null;
  const hasCurrentDetail = detail?.username === params.username;
  const hasCurrentError = errorUsername === params.username && !!error;
  const profileDetail = hasCurrentDetail ? detail : allProfileDetail;
  const profileError = invalidUsername
    ? errorDescriptions({ status: 404 })
    : isAllProfile
      ? null
      : hasCurrentError
        ? error
        : null;
  const profileLoading = !invalidUsername && !isAllProfile && !hasCurrentDetail && !hasCurrentError;

  useEffect(() => {
    setRouter({
      location,
      navigate
    });

    if (
      Object.values(permissions.PAGES).find(
        (p) =>
          window.location.href.includes(window.location.origin + p.path) ||
          window.location.href.includes('/admin')
      )
    )
      return navigate('/');

    if (detail?.username === params.username) return;
    if (invalidUsername || isAllProfile) return;

    model.accountDetail(params.username, (error, data) => {
      if (!error && (data?.isDoctor || data?.isClinic)) {
        setError(null);
        setErrorUsername(null);
        setDetail({ username: params.username, ...data });
        // if (deviceIsMobile && !model.isLogged) {
        //   permissions.history = {
        //     url: location.pathname,
        //     params: { isRedirect: true }
        //   };
        //   return navigate('/login');
        // }

        if (data.isDoctor && data.status === constants.general.userStatus.disabled) {
          AlertManager.popup(AlertManager.TYPES.INFO, {
            maxWidth: 450,
            close: false,
            title: lang.t(
              data.clinicIds?.length > 0 && data.clinic?.username
                ? LANG_CONST.notice.disabled_clinic_doctor
                : LANG_CONST.notice.disabled_doctor,
              {
                doctor: upperFirstCharAll(userFullName(data))
              }
            ),

            actionButton: {
              label: LANG_CONST.buttons.ok,
              action: (props) => props.hide()
            }
          });
        }

        if (data.isDoctor && data.clinicIds?.length > 0 && data.clinic?.username) {
          navigate('/' + data.clinic?.username, { replace: true, state: { doctor: data } });
        }
      } else {
        setErrorUsername(params.username);
        setError(errorDescriptions({ status: 404 }));
      }
    });
  }, [detail?.username, invalidUsername, isAllProfile, location, navigate, params.username]);

  return (
    <div
      className={'mc-page-main'}
      style={
        screen.isSmallScreen ? {} : { flexDirection: 'column', height: 'auto', minHeight: '100%' }
      }
    >
      <div className={'mc-container'}>
        {profileLoading ? (
          <div className={'mc-m-auto'}>
            <Spinner />
          </div>
        ) : profileError ? (
          <NotFound screen={screen} />
        ) : profileDetail?.isDoctor ? (
          <Doctor />
        ) : (
          <Clinic clinic={profileDetail} />
        )}
      </div>
    </div>
  );
};

export default Profile;
